import { Illustration, Text, type UseModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { Box, Stack, ThemeProvider } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { FullScreenDialog } from "@src/appV2/lib/Dialogs/FullScreen/Dialog";
import { formatUsPhoneNumber } from "@src/appV2/lib/utils";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { PageHeaderTopRow } from "@src/appV2/redesign/components/PageHeaderTopRow";
import { type ReactElement, useEffect } from "react";

interface Props {
  modalState: UseModalState;
  phoneNumber: string;
  onLogin: () => void;
  onUseDifferentPhoneNumber: () => void;
}

export function PhoneNumberExistsDialog(props: Props): ReactElement {
  const { modalState, phoneNumber, onLogin, onUseDifferentPhoneNumber } = props;

  const theme = getTheme();
  const formattedPhoneNumber = formatUsPhoneNumber(phoneNumber);

  useEffect(() => {
    if (modalState.modalIsOpen) {
      logEvent(APP_V2_USER_EVENTS.VIEWED_PHONE_NUMBER_EXISTS_DIALOG, {
        phoneNumber,
      });
    }
  }, [modalState.modalIsOpen, phoneNumber]);

  return (
    <ThemeProvider theme={theme}>
      <FullScreenDialog modalState={modalState} variant="tertiary">
        <PageHeaderTopRow
          onClickBack={() => {
            modalState.closeModal();
            logEvent(APP_V2_USER_EVENTS.CLOSE_PHONE_NUMBER_EXISTS_DIALOG, {
              phoneNumber,
              action: "GO_BACK",
            });
          }}
        />

        <Box
          sx={{
            flex: 1,
            padding: theme.spacing(6),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Stack
            spacing={8}
            sx={{
              textAlign: "center",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Illustration type="warning" />

            <Text variant="h2" sx={{ textAlign: "center" }}>
              Phone number already in use
            </Text>

            <Text variant="body1" sx={{ textAlign: "center", color: "#3D414B" }}>
              You entered{" "}
              <Text inline bold variant="body1" sx={{ color: "#0C1C43" }}>
                {formattedPhoneNumber || phoneNumber}
              </Text>
              , which is linked to an existing Clipboard account. Log in to continue or use a
              different phone number to sign up.
            </Text>
          </Stack>
        </Box>

        <DialogFooter orientation="vertical">
          <Button
            fullWidth
            variant="contained"
            size="large"
            aria-label="Log In"
            onClick={() => {
              modalState.closeModal();
              onLogin();
              logEvent(APP_V2_USER_EVENTS.SELECT_PHONE_NUMBER_EXISTS_LOGIN, {
                phoneNumber,
                action: "LOG_IN",
              });
            }}
          >
            Log In
          </Button>
          <Button
            fullWidth
            variant="outlined"
            size="large"
            aria-label="Use a different phone number"
            onClick={() => {
              modalState.closeModal();
              onUseDifferentPhoneNumber();
              logEvent(APP_V2_USER_EVENTS.CLOSE_PHONE_NUMBER_EXISTS_DIALOG, {
                phoneNumber,
                action: "SIGN_UP",
              });
            }}
          >
            Use a different phone number
          </Button>
        </DialogFooter>
      </FullScreenDialog>
    </ThemeProvider>
  );
}
